import { ResolveFn, Routes } from '@angular/router';
import { inject } from '@angular/core';
import { catchError, map, of } from 'rxjs';
import { MarketingLayoutComponent } from './marketing/layout/marketing-layout.component';
import { HomePageComponent } from './marketing/pages/home/home-page.component';
import { Home1PageComponent } from './marketing/pages/home1/home1-page.component';
import { LeadFormPageComponent } from './marketing/pages/lead-form/lead-form-page.component';
import { Country1PageComponent } from './marketing/pages/country1/country1-page.component';
import { CountryPageComponent } from './marketing/pages/country/country-page.component';
import { MarketingSuccessStoriesPageComponent } from './marketing/pages/success-stories/success-stories-page.component';
import { MarketingSuccessStoriesAllPageComponent } from './marketing/pages/success-stories/success-stories-all-page.component';
import { MarketingTestimonialsPageComponent } from './marketing/pages/testimonials/testimonials-page.component';
import { MarketingTestimonialsAllPageComponent } from './marketing/pages/testimonials/testimonials-all-page.component';
import { MarketingBlogsListingPageComponent } from './marketing/pages/blogs/blogs-listing-page.component';
import { MarketingBlogsCategoryPageComponent } from './marketing/pages/blogs/blogs-category-page.component';
import { MarketingBlogsCountryPageComponent } from './marketing/pages/blogs/blogs-country-page.component';
import { MarketingBlogDetailPageComponent } from './marketing/pages/blogs/blog-detail-page.component';
import { MarketingBlogPreviewPageComponent } from './marketing/pages/blogs/blog-preview-page.component';
import { PlatformLayoutComponent } from './platform/layout/platform-layout.component';
import { DashboardPageComponent } from './platform/pages/dashboard/dashboard-page.component';
import { LoginPageComponent } from './auth/login/login-page.component';
import { authGuard } from './shared/guards/auth.guard';
import { SettingsPageComponent } from './platform/pages/settings/settings-page.component';
import { ReleaseNotesPageComponent } from './platform/pages/settings/release-notes/release-notes-page.component';
import { CountriesPageComponent } from './platform/pages/countries/countries-page.component';
import { CountryFormPageComponent } from './platform/pages/countries/country-form-page.component';
import { TestimonialsPageComponent } from './platform/pages/testimonials/testimonials-page.component';
import { UniversitiesPageComponent } from './platform/pages/universities/universities-page.component';
import { HomeMarketingPageComponent } from './platform/pages/home-marketing/home-marketing-page.component';
import { SuccessStoriesPageComponent } from './platform/pages/success-stories/success-stories-page.component';
import { PlatformBlogsListPageComponent } from './platform/pages/blogs/blogs-list-page.component';
import { PlatformBlogEditorPageComponent } from './platform/pages/blogs/blog-editor-page.component';
import { PlatformBlogCategoriesPageComponent } from './platform/pages/blogs/blog-categories-page.component';
import { PlatformBlogAuthorsPageComponent } from './platform/pages/blogs/blog-authors-page.component';
import { PlatformBlogSubscribersPageComponent } from './platform/pages/blogs/blog-subscribers-page.component';
import { MediaPageComponent } from './platform/pages/media/media-page.component';
import { ApplicantsPageComponent } from './platform/pages/applicants/applicants-page.component';
import { LeadsPageComponent } from './platform/pages/leads/leads-page.component';
import { ApplicantDetailPageComponent } from './shared/pages/applicant-detail-page.component';
import { LeadDetailPageComponent } from './shared/pages/lead-detail-page.component';
import { UsersPageComponent } from './platform/pages/users/users-page.component';
import { RolesPermissionsPageComponent } from './platform/pages/roles-permissions/roles-permissions-page.component';
import { LeadStatusesPageComponent } from './platform/pages/crm/lead-statuses-page.component';
import { LeadStatusTransitionsPageComponent } from './platform/pages/crm/lead-status-transitions-page.component';
import { FranchisesPageComponent } from './platform/pages/franchises/franchises-page/franchises-page.component';
import { FranchiseFormPageComponent } from './platform/pages/franchises/franchise-form-page/franchise-form-page.component';
import { FranchiseDetailPageComponent } from './platform/pages/franchises/franchise-detail-page/franchise-detail-page.component';
import { FeatureRequestsPageComponent } from './platform/pages/feature-requests/feature-requests-page.component';
import { FeatureRequestDetailPageComponent } from './platform/pages/feature-requests/feature-request-detail-page.component';
import { TenantLayoutComponent } from './tenant/layout/tenant-layout.component';
import { TenantDashboardPageComponent } from './tenant/pages/dashboard/tenant-dashboard-page.component';
import { TenantApplicantsPageComponent } from './tenant/pages/applicants/tenant-applicants-page.component';
import { TenantLeadsPageComponent } from './tenant/pages/leads/tenant-leads-page.component';
import { TenantStaffPageComponent } from './tenant/pages/staff/tenant-staff-page.component';
import { TenantSettingsPageComponent } from './tenant/pages/settings/tenant-settings-page.component';
import { tenantAuthGuard } from './shared/guards/tenant-auth.guard';
import { CountriesService } from './shared/services/countries.service';

const countryResolver: ResolveFn<unknown> = (route) =>
  inject(CountriesService)
    .getPublicCountryPage(route.paramMap.get('slug') ?? '')
    .pipe(
      map((page) => ({ error: false as const, page })),
      catchError(() => of({ error: true as const })),
    );

export const routes: Routes = [
  /**
   * Static premium homepage benchmark. Renders standalone (no marketing layout
   * wrapper, no CMS resolver) so the page owns its full vertical composition.
   */
  { path: 'home1', component: Home1PageComponent },
  { path: 'lead-form', component: LeadFormPageComponent },
  { path: 'country1', component: Country1PageComponent },
  {
    path: '',
    component: MarketingLayoutComponent,
    children: [
      { path: '', component: HomePageComponent },
      { path: 'success-stories/all', component: MarketingSuccessStoriesAllPageComponent },
      { path: 'success-stories', component: MarketingSuccessStoriesPageComponent },
      { path: 'testimonials/all', component: MarketingTestimonialsAllPageComponent },
      { path: 'testimonials', component: MarketingTestimonialsPageComponent },
      { path: 'blogs', component: MarketingBlogsListingPageComponent },
      { path: 'blogs/category/:slug', component: MarketingBlogsCategoryPageComponent },
      { path: 'blogs/country/:slug', component: MarketingBlogsCountryPageComponent },
      { path: 'blog/preview/:token', component: MarketingBlogPreviewPageComponent },
      { path: 'blog/:slug', component: MarketingBlogDetailPageComponent },
      { path: 'countries/:slug', component: CountryPageComponent, resolve: { countryPage: countryResolver } },
    ],
  },
  { path: 'auth/login', component: LoginPageComponent },
  { path: 'tenant/login', redirectTo: 'auth/login', pathMatch: 'full' },
  {
    path: 'tenant',
    component: TenantLayoutComponent,
    canActivate: [tenantAuthGuard],
    canActivateChild: [tenantAuthGuard],
    children: [
      { path: 'dashboard', component: TenantDashboardPageComponent },
      { path: 'leads', component: TenantLeadsPageComponent },
      { path: 'leads/:id', component: LeadDetailPageComponent, data: { leadArea: 'tenant' } },
      { path: 'applicants', component: TenantApplicantsPageComponent },
      { path: 'applicants/:id', component: ApplicantDetailPageComponent, data: { applicantArea: 'tenant' } },
      { path: 'staff', component: TenantStaffPageComponent },
      { path: 'settings', component: TenantSettingsPageComponent },
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
    ],
  },
  {
    path: 'platform',
    component: PlatformLayoutComponent,
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    children: [
      { path: 'dashboard', component: DashboardPageComponent },
      { path: 'franchises', component: FranchisesPageComponent },
      { path: 'franchises/new', component: FranchiseFormPageComponent },
      { path: 'franchises/:franchiseId', component: FranchiseDetailPageComponent },
      { path: 'countries', component: CountriesPageComponent },
      { path: 'countries/new', component: CountryFormPageComponent },
      { path: 'countries/:id/edit', component: CountryFormPageComponent },
      { path: 'universities', component: UniversitiesPageComponent },
      { path: 'marketing/home', component: HomeMarketingPageComponent },
      { path: 'success-stories', component: SuccessStoriesPageComponent },
      { path: 'testimonials', component: TestimonialsPageComponent },
      { path: 'blogs', component: PlatformBlogsListPageComponent },
      { path: 'blogs/new', component: PlatformBlogEditorPageComponent },
      { path: 'blogs/:id/edit', component: PlatformBlogEditorPageComponent },
      { path: 'blog-categories', component: PlatformBlogCategoriesPageComponent },
      { path: 'blog-authors', component: PlatformBlogAuthorsPageComponent },
      { path: 'blog-subscribers', component: PlatformBlogSubscribersPageComponent },
      { path: 'media', component: MediaPageComponent },
      { path: 'leads', component: LeadsPageComponent },
      { path: 'leads/:id', component: LeadDetailPageComponent, data: { leadArea: 'platform' } },
      { path: 'applicants', component: ApplicantsPageComponent },
      { path: 'applicants/:id', component: ApplicantDetailPageComponent, data: { applicantArea: 'platform' } },
      { path: 'feature-requests', component: FeatureRequestsPageComponent },
      { path: 'feature-requests/:id', component: FeatureRequestDetailPageComponent },
      {
        path: 'crm/lead-statuses',
        component: LeadStatusesPageComponent,
        data: { crmFeatures: 'leads' },
      },
      {
        path: 'crm/lead-statuses/:id/transitions',
        component: LeadStatusTransitionsPageComponent,
        data: { crmFeatures: 'leads' },
      },
      {
        path: 'crm/applicant-statuses',
        component: LeadStatusesPageComponent,
        data: { crmFeatures: 'applicants' },
      },
      {
        path: 'crm/applicant-statuses/:id/transitions',
        component: LeadStatusTransitionsPageComponent,
        data: { crmFeatures: 'applicants' },
      },
      { path: 'users', component: UsersPageComponent },
      { path: 'roles-permissions', component: RolesPermissionsPageComponent },
      { path: 'settings', component: SettingsPageComponent },
      { path: 'settings/release-notes', component: ReleaseNotesPageComponent },
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
    ],
  },
  { path: 'app', pathMatch: 'full', redirectTo: 'tenant/dashboard' },
  { path: '**', redirectTo: '' },
];
